import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, timeout } from 'rxjs/operators';
import { StaffProfileService, StaffDateOfBirthStatus } from '../../shared/staff-profile/staff-profile.service';

/**
 * Staff who haven't entered their date of birth go to /complete-profile before
 * any Board page (all staff except Entity Owners; the server decides).
 *
 * - canActivate (entering /pages, e.g. right after login): always asks the server.
 * - canActivateChild (every click inside /pages): uses the cached answer.
 *
 * Fails open on error or a slow answer (same as the lock guard), so a server
 * hiccup never traps everyone outside the Board.
 */
@Injectable()
export class ProfileCompletionGuard implements CanActivate, CanActivateChild {
  constructor(private staffProfile: StaffProfileService, private router: Router) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.fetchAndDecide();
  }

  canActivateChild(): Observable<boolean | UrlTree> | boolean | UrlTree {
    const cached = this.staffProfile.cachedStatus;
    if (cached) return this.decide(cached);
    return this.fetchAndDecide();
  }

  private fetchAndDecide(): Observable<boolean | UrlTree> {
    return this.staffProfile.getDateOfBirthStatus().pipe(
      timeout(6000),
      map((status) => this.decide(status)),
      // The error interceptor answers some failures (no connection) with no
      // value at all: treat that as "don't block" too.
      defaultIfEmpty(true as boolean | UrlTree),
      catchError(() => of(true))
    );
  }

  private decide(status: StaffDateOfBirthStatus): boolean | UrlTree {
    return status?.required ? this.router.parseUrl('/complete-profile') : true;
  }
}
